<template>
  <loading-placeholder v-if="isLoading && !hasCachedBreadcrumbData" :lines="1" class="listing-header__loading" />
  <div v-else class="listing-header">
    <sample-ribbon v-if="showSampleRibbon" size="small" />
    <!-- If cached breadcrumb info is for this project, expose while prop data is loading -->
    <breadcrumb-for-listing
      v-if="isLoading && hasCachedBreadcrumbData"
      :is-custom-filter-submitting="isCustomFilterSubmitting"
      :entity-type="entityType"
      :title="cachedBreadcrumbData.title"
      :selected-entity-id="cachedBreadcrumbData.selectedEntityID"
      :default-filters="cachedBreadcrumbData.defaultFilters"
      :custom-filters="cachedBreadcrumbData.customFilters"
      :current-filter="cachedBreadcrumbData.currentFilter"
      :total-item-count="cachedBreadcrumbData.totalItemCount"
    />
    <breadcrumb-for-listing
      v-else
      :is-custom-filter-submitting="isCustomFilterSubmitting"
      :entity-type="entityType"
      :title="title"
      :selected-entity-id="entityId"
      :default-filters="defaultFilters"
      :custom-filters="customFilters"
      :current-filter="currentFilter"
      :is-kanban="isKanban"
      :total-item-count="totalItemCount"
      :active-assignee-counts="activeAssigneeCounts"
      @save-new-filter="(name) => $emit('save-new-filter', name)"
      @update-custom-filter-name="(name) => $emit('update-custom-filter-name', name)"
      @change-filter="(id) => $emit('change-filter', id)"
      @delete-custom-filter="(id) => handleDeleteCustomFilter(id)"
      @delete-current-custom-filter="handleDeleteCustomFilter(currentFilter.value)"
      @select-assignee="(id) => $emit('select-assignee', id)"
    />
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent, PropType } from 'vue'
import {
  ContentPlaceholders,
  ContentPlaceholdersText,
} from '@/components/global/content-placeholders'
import {
  IBreadcrumbDataFromListing,
  INavMenuItem,
} from '@/types/data-objects/global'

/* Mixins */

/* Components */

/* Helpers */
import BreadcrumbAssistant from '@/helpers/breadcrumb-assistant'

/* Data types */
import { EEntities } from '@/types'

/* API */

export default defineComponent({
  name: 'ListingHeader',

  components: {
    ContentPlaceholders,
    ContentPlaceholdersText,
  },

  props: {
    title: {
      type: String,
      default: '',
    },

    entityId: {
      type: Number,
      default: null,
    },

    entityType: {
      type: String as () => EEntities,
      required: true,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    isCustomFilterSubmitting: {
      type: Boolean,
      default: false,
    },

    isKanban: {
      type: Boolean,
      default: false,
    },

    showSampleRibbon: {
      type: Boolean,
      default: false,
    },

    defaultFilters: {
      type: Array as () => INavMenuItem[],
      default: (): INavMenuItem[] => [],
    },

    customFilters: {
      type: Array as () => INavMenuItem[],
      default: (): INavMenuItem[] => [],
    },

    currentFilter: {
      type: Object as () => INavMenuItem,
      required: true,
    },

    totalItemCount: {
      type: Number,
      default: null,
    },

    activeAssigneeCounts: {
      type: Array as PropType<{ id: number; name: string; count: number }[]>,
      default: (): any[] => [],
    },
  },

  emits: [
    'change-filter',
    'delete-custom-filter',
    'save-new-filter',
    'select-assignee',
    'update-custom-filter-name',
  ],

  data() {
    return {
      EEntities,
    }
  },

  computed: {
    cachedBreadcrumbData(): IBreadcrumbDataFromListing {
      return BreadcrumbAssistant.lastVisitedBreadcrumbDataFromListing(
        )
    },

    hasCachedBreadcrumbData(): boolean {
      if (!this.cachedBreadcrumbData) {
        return false
      }

      const breadcrumbMatchesEntityLanding =
        this.entityId !== null &&
        this.cachedBreadcrumbData.selectedEntityID === this.entityId

      const breadcrumbMatchesAllLanding =
        this.entityId === null &&
        this.cachedBreadcrumbData.selectedEntityID === null &&
        this.cachedBreadcrumbData.entityType === this.entityType

      return breadcrumbMatchesEntityLanding || breadcrumbMatchesAllLanding
    },

    internalCurrentFilter(): INavMenuItem {
      if (this.hasCachedBreadcrumbData) {
        // Will always be up-to-date since any current filter change will be watched
        // by breadcrumb-for-entity-landing and update the cache
        return this.cachedBreadcrumbData.currentFilter
      }
      return this.currentFilter
    },
  },

  methods: {
    handleDeleteCustomFilter(identifier: string) {
      this.$confirm(
        `<strong>This cannot be undone.</strong>
        <br>Are you sure you want to delete this?`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: 'Delete this custom filter?',
        },
      ).then(() => {
        this.$emit(`delete-custom-filter`, identifier)
      })
    },
  },
})
</script>

<style lang="scss">
.listing-header {
  @include single-line-ellipsis;
  display: flex;
  align-items: center;
  width: 100%;

  .sample-ribbon {
    top: 20px;
    left: 10px;

    @include media-breakpoint-up(lg) {
      top: 30px;
      left: -26px;
    }
  }

  @include media-breakpoint-down(sm) {
    padding-right: $detail-gutter-pad-sm;
    padding-left: $detail-gutter-pad-sm;
    margin-bottom: 0;
  }

  &__loading {
    position: relative;
    // Makes it look good on mobile too (with 10% side padding on vertical stacked layout)
    width: 90%;
    height: 32px;

    .vue-content-placeholders-text__line {
      position: absolute;
      width: 100% !important;
      height: 100%;
    }
  }
}
</style>
